import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import TopicCard from '@/components/feature/TopicCard';
import useSeo from '@/hooks/useSeo';
import { categories, topics } from '@/mocks/topics';

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? '';
  const [keyword, setKeyword] = useState(searchParams.get('q') ?? '');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return topics.filter((topic) => {
      const matchCategory = activeCategory ? topic.category === activeCategory : true;
      const matchKeyword = kw
        ? `${topic.title}${topic.summary}${topic.tags.join('')}`.toLowerCase().includes(kw)
        : true;
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const setCategory = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('category', key);
    else next.delete('category');
    if (keyword.trim()) next.set('q', keyword.trim());
    setSearchParams(next);
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set('q', value.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  useSeo({
    title: '议题追踪 - 社会爆料行业内幕与乱象盘点｜锐察社',
    description:
      '按分类查看社会爆料议题追踪，涵盖行业内幕、消费乱象、维权纠纷与争议事件。支持关键词搜索与分类筛选，持续更新。',
    keywords: '社会爆料,社会爆料行业内幕,社会爆料乱象,社会爆料维权,社会爆料纠纷,议题盘点',
    path: '/topics',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '议题追踪',
        url: 'https://sjbxw.cn/topics',
        description: '按分类查看社会爆料议题追踪，涵盖行业内幕、消费乱象、维权纠纷与争议事件。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sjbxw.cn/' },
          { '@type': 'ListItem', position: 2, name: '议题追踪', item: 'https://sjbxw.cn/topics' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: topics.slice(0, 10).map((topic, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://sjbxw.cn/topics/${topic.slug}`,
          name: topic.title,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-300 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="font-label text-sm text-foreground-500">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="transition-colors hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-foreground-800">议题追踪</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            议题追踪
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            把分散的爆料整理成有脉络的议题。你可以按分类查看，也可以直接搜索关键词，找到与自己处境相关的讨论。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory('')}
              className={`rounded-md px-4 py-2 font-label text-sm transition-colors ${
                activeCategory === ''
                  ? 'bg-foreground-900 text-background-50'
                  : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => setCategory(cat.key)}
                className={`rounded-md px-4 py-2 font-label text-sm transition-colors ${
                  activeCategory === cat.key
                    ? 'bg-primary-600 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {cat.key}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="搜索议题关键词"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
            />
          </div>
        </div>

        <p className="mt-5 font-label text-sm text-foreground-500">
          共找到 <span className="text-foreground-900">{filtered.length}</span> 个议题
          {activeCategory && ` · 分类：${activeCategory}`}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((topic) => (
              <TopicCard key={topic.slug} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-400 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              暂未找到相关议题
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试试更换关键词，或切换到其他分类查看。也欢迎把线索反馈给我们。
            </p>
            <button
              type="button"
              onClick={() => { setKeyword(''); setCategory(''); }}
              className="mt-5 rounded-md bg-primary-600 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-700"
            >
              清空筛选条件
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}